import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, Service, SortOrderDirection } from 'webapp-types';
import ActivityLogEventFilterGroups from 'webapp/const/activity-log-event-filter';
import { ActivityLogFeature } from 'webapp/enums/activity-log';
import { OrderDirection } from 'webapp/enums/enums';
import { sortBy } from 'webapp/utils/common';
import type { SearchProps } from 'webapp/utils/filter';

import type ActivityLog from 'webapp/models-custom/activity-log';
import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type UserRecord from 'webapp/models/user';

export enum LogFilterId {
	TIME_FRAME = 1,
	USERS = 2,
	FEATURE = 3,
	ACCOUNT = 4,
	EVENT = 5,
}

export const ALL_ACCOUNTS = 0;
export const ALL_EVENTS = 0;
export const ALL_USERS = 0;

export enum TimeFrame {
	ALL_TIME = 0,
	TODAY = 1,
	LAST_7_DAYS = 2,
	LAST_30_DAYS = 3,
	LAST_90_DAYS = 4,
}

export default class FilterSortActivityLog extends FilterSort<ActivityLog> {
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;

	@tracked override sortProp = 'created';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;
	@tracked accountUsers: APIData.Settings.ActivityLogUser[] | null = null;

	override settingsNamePrefix = 'activityLogFilterSettingsV2';

	@tracked override searchPlaceholder = 'Search activity...';

	override searchProps: SearchProps = {
		sanitizedMessage: 2,
		'user.name': 1,
	};

	constructor(options: FilterSortSettings<ActivityLog>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		// Account
		const agencySpaceId = this.agencySpaceId;
		if (agencySpaceId) {
			const savedAccount = selectedFilters[LogFilterId.ACCOUNT]?.[0] as number | undefined;
			const isSavedAccountAvailable = this.accountOptions.some(
				(option) => option.id === savedAccount,
			);

			this.createFilter<number>({
				id: LogFilterId.ACCOUNT,
				label: 'Account',
				name: 'account',
				selected: isSavedAccountAvailable ? savedAccount : undefined,
				defaultValue: agencySpaceId,
				searchableProperties: ['text'],
				getOptions: () => {
					return this.accountOptions;
				},
				onSelectedChange: () => {
					this.resetUsersFilter();
				},
			});
		}

		// Time frame
		this.createFilter<number>({
			id: LogFilterId.TIME_FRAME,
			label: 'Time frame',
			name: 'time frame',
			selected: selectedFilters[LogFilterId.TIME_FRAME] as number | undefined,
			defaultValue: TimeFrame.LAST_30_DAYS,
			getOptions: () => {
				return this.timeFrameOptions;
			},
		});

		// Users
		this.createFilter<number>({
			id: LogFilterId.USERS,
			label: 'Users',
			name: 'users',
			selected: selectedFilters[LogFilterId.USERS] as number | undefined,
			getOptions: () => {
				return this.userOptions;
			},
		});

		// Feature category
		this.createFilter<number>({
			id: LogFilterId.FEATURE,
			label: 'Feature',
			name: 'feature',
			selected: selectedFilters[LogFilterId.FEATURE] as number | undefined,
			getOptions: () => {
				return this.featureOptions;
			},
			onSelectedChange: () => {
				this.dropUnavailableEvents();
			},
		});

		// Event
		const savedEvents = (selectedFilters[LogFilterId.EVENT] as number[] | undefined)?.filter(
			(id) => id > ALL_EVENTS,
		);

		this.createFilter<number>({
			id: LogFilterId.EVENT,
			label: 'Event',
			name: 'event',
			selected: savedEvents?.length ? savedEvents : undefined,
			isMultiSelect: true,
			searchableProperties: ['text'],
			getOptions: () => {
				return this.eventOptions;
			},
			onSelectedChange: () => {
				this.dropUnavailableEvents();
			},
		});
	}

	get agencySpaceId(): number | undefined {
		return this.accountService.isAgency ? this.accountService.id : undefined;
	}

	@cached
	get accountOptions(): FilterOption<number>[] {
		const agencySpaceId = this.agencySpaceId;
		if (!agencySpaceId) {
			return [];
		}

		const list: FilterOption<number>[] = [
			{ id: ALL_ACCOUNTS, text: 'All' },
			{ id: agencySpaceId, text: 'My account' },
		];

		sortBy(this.mmSpacesService.otherSpaceRecords, 'companyName').forEach(
			(clientSpaceRecord) => {
				list.push({
					id: +clientSpaceRecord.id!,
					text: clientSpaceRecord.companyName,
					img: '',
					color: clientSpaceRecord.color,
				});
			},
		);

		return list;
	}

	get selectedFeature(): ActivityLogFeature | null {
		const featureFilter = this.findFilter(LogFilterId.FEATURE);
		const feature = featureFilter?.selected[0] as ActivityLogFeature | undefined;
		return feature || null;
	}

	@cached
	get eventOptions(): FilterOption<number>[] {
		const selectedFeature = this.selectedFeature,
			featureOptions = this.featureOptions;
		const list: FilterOption<number>[] = [{ id: ALL_EVENTS, text: 'All events' }];

		ActivityLogEventFilterGroups.forEach((group) => {
			if (selectedFeature && group.feature !== selectedFeature) {
				return;
			}

			list.push({
				id: -group.feature,
				text: featureOptions.find((option) => option.id === group.feature)?.text ?? '',
				isDisabled: true,
				class: 'activity-log-event-group',
			});
			group.events.forEach((event) => {
				list.push({ id: event.id, text: event.text });
			});
		});

		return list;
	}

	get timeFrameOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [
			{ id: TimeFrame.ALL_TIME, text: 'All time' },
			{ id: TimeFrame.TODAY, text: 'Today' },
			{ id: TimeFrame.LAST_7_DAYS, text: 'Last 7 days' },
			{ id: TimeFrame.LAST_30_DAYS, text: 'Last 30 days' },
			{ id: TimeFrame.LAST_90_DAYS, text: 'Last 90 days' },
		];

		return list;
	}

	@cached
	get userOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [{ id: ALL_USERS, text: 'Everyone' }],
			allItemCountByOwner: Record<number, number> = {};

		this.allItems.forEach((item: ActivityLog) => {
			const ownerId = item.userId;
			if (!allItemCountByOwner[ownerId]) {
				allItemCountByOwner[ownerId] = 0;
			}
			allItemCountByOwner[ownerId]++;
		});

		const users =
			this.accountUsers ??
			this.mmUsersService.savedRecords.map((user: UserRecord) => ({
				id: +user.id!,
				name: user.name,
				avatar: user.avatar,
			}));

		users.forEach((user) => {
			list.push({
				id: user.id,
				text: user.name ?? '',
				img: user.avatar || '',
				used: allItemCountByOwner[user.id] || 0,
			});
		});

		return list;
	}

	get featureOptions(): FilterOption<number>[] {
		const list: FilterOption<number>[] = [
			{ id: 0, text: 'All' },
			{ id: ActivityLogFeature.BOARDS, text: 'Dashboards' },
			{ id: ActivityLogFeature.LOOPS, text: 'Looped dashboards' },
			{ id: ActivityLogFeature.METRICS, text: 'Metrics' },
			{ id: ActivityLogFeature.REPORTS, text: 'Reports' },
			{ id: ActivityLogFeature.DATA_SOURCES, text: 'Data sources' },
			{ id: ActivityLogFeature.GOALS, text: 'Goals' },
			{ id: ActivityLogFeature.SCORECARDS, text: 'Scorecards' },
			{ id: ActivityLogFeature.ALERTS, text: 'Alerts' },
			{ id: ActivityLogFeature.SNAPSHOTS, text: 'Scheduled snapshots' },
			{ id: ActivityLogFeature.GENIE, text: 'Genie' },
			{ id: ActivityLogFeature.PLANS_AND_BILLING, text: 'Plans & billing' },
			{ id: ActivityLogFeature.USERS, text: 'Users' },
			{ id: ActivityLogFeature.ACCOUNT, text: 'Account' },
			{ id: ActivityLogFeature.COMPANY_INFORMATION, text: 'Company information' },
			{ id: ActivityLogFeature.SSO, text: 'Single sign-on (SSO)' },
		];

		return list;
	}

	private resetUsersFilter(): void {
		const usersFilter = this.findFilter(LogFilterId.USERS);
		if (usersFilter && !usersFilter.isDefaultSelected) {
			usersFilter.selectOption(ALL_USERS);
		}
	}

	private dropUnavailableEvents(): void {
		const eventFilter = this.findFilter(LogFilterId.EVENT);
		if (!eventFilter || eventFilter.isDefaultSelected) {
			return;
		}

		const selectedEventIds = eventFilter.selected as number[];
		const availableEventIds = this.eventOptions
			.filter((option) => option.id > ALL_EVENTS)
			.map((option) => option.id);
		const remainingEventIds = selectedEventIds.filter((id) => availableEventIds.includes(id));

		if (remainingEventIds.length === selectedEventIds.length) {
			return;
		}

		eventFilter.selectOption(remainingEventIds.length ? remainingEventIds : [ALL_EVENTS]);
	}
}
